import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Download, Loader2, CheckCircle2, X, Film, Layers, RotateCcw, Settings2, AlertTriangle, Ban, Gauge, Zap, ChevronDown, ChevronRight, Play, Pause } from "lucide-react";
import { api, mediaUrl, resolveUrl } from "@/lib/api";
import { fmtBytes, fmtTime } from "@/lib/format";
import { canLocalExport, exportLocal, exportCaptionLayerAlpha } from "@/lib/renderEngine/localExport";
import { chooseRenderPath } from "@/lib/renderEngine";
import { exportTimelineOnDevice, canDeviceExport } from "@/lib/renderEngine/fullDeviceExport";
import { isFeatureEnabled } from "@/lib/features";
import { ShareVideoButton } from "@/components/ShareVideoButton";

// API errors may carry detail as a string or {message}; always return text (objects crash React).
const detailMsg = (e) => { const d = e?.response?.data?.detail; return (typeof d === "string" ? d : d?.message || d?.error) || "Export failed to start"; };

function BlobVideo({ path, ...props }) {
  return <video src={mediaUrl(path)} {...props} />;
}

// Progressive export preview: plays the REAL rolling MP4 the backend assembles from
// already-completed render segments. It refetches (with a version bust) and restores the
// playback position as more output becomes available, so the user watches the export extend.
// Includes a mini scrubber so users can jump back through the already-rendered portion.
function ExportPreviewPlayer({ url, seconds }) {
  const ref = useRef(null);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [scrubbing, setScrubbing] = useState(false);
  const src = url ? `${resolveUrl(url)}${url.includes("?") ? "&" : "?"}v=${Math.floor(seconds || 0)}` : null;
  useEffect(() => {
    const v = ref.current;
    if (!v || !src) return;
    const wasPlaying = !v.paused && !v.ended;
    const pos = v.currentTime || 0;
    v.src = src;
    v.load();
    const onMeta = () => {
      try { if (pos > 0 && pos < (v.duration || 0)) v.currentTime = pos; } catch { /* noop */ }
      if (wasPlaying) v.play().catch(() => {});
      v.removeEventListener("loadedmetadata", onMeta);
    };
    v.addEventListener("loadedmetadata", onMeta);
    return () => v.removeEventListener("loadedmetadata", onMeta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);
  if (!src) return null;
  const ready = dur || Number(seconds || 0);
  const seek = (t) => { const v = ref.current; if (v && isFinite(t)) { v.currentTime = t; setCur(t); } };
  const toggle = () => { const v = ref.current; if (!v) return; if (v.paused) v.play().catch(() => {}); else v.pause(); };
  return (
    <div className="relative h-full w-full">
      <video
        ref={ref} autoPlay muted playsInline
        className="h-full w-full object-contain"
        data-testid="export-live-preview-video"
        onLoadedMetadata={(e) => setDur(e.currentTarget.duration || 0)}
        onDurationChange={(e) => setDur(e.currentTarget.duration || 0)}
        onTimeUpdate={(e) => { if (!scrubbing) setCur(e.currentTarget.currentTime || 0); }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onClick={toggle}
      />
      {/* Mini scrubber for the already-rendered portion */}
      <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/80 to-transparent px-2.5 pb-2 pt-6" data-testid="export-preview-scrubber">
        <button type="button" onClick={toggle} data-testid="export-preview-playpause"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25">
          {playing ? <Pause size={12} /> : <Play size={12} />}
        </button>
        <input
          type="range" min={0} max={ready > 0 ? ready : 0} step={0.05} value={Math.min(cur, ready)}
          data-testid="export-preview-seek"
          onMouseDown={() => setScrubbing(true)}
          onTouchStart={() => setScrubbing(true)}
          onChange={(e) => { setCur(Number(e.target.value)); seek(Number(e.target.value)); }}
          onMouseUp={() => setScrubbing(false)}
          onTouchEnd={() => setScrubbing(false)}
          className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-white/25 accent-[#00F0FF]"
          style={{ background: `linear-gradient(to right, #00F0FF ${ready > 0 ? (Math.min(cur, ready) / ready) * 100 : 0}%, rgba(255,255,255,0.25) ${ready > 0 ? (Math.min(cur, ready) / ready) * 100 : 0}%)` }}
        />
        <span className="shrink-0 text-[10px] tabular-nums text-white/90" data-testid="export-preview-time">
          {clock(cur) || "00:00"} / {clock(ready) || "00:00"}
        </span>
      </div>
    </div>
  );
}

const RESOLUTIONS = [
  { l: "1080 × 1920 (9:16)", w: 1080, h: 1920 },
  { l: "1080 × 1080 (1:1)", w: 1080, h: 1080 },
  { l: "1080 × 1350 (4:5)", w: 1080, h: 1350 },
  { l: "1920 × 1080 (16:9)", w: 1920, h: 1080 },
  { l: "720 × 1280 (9:16)", w: 720, h: 1280 },
];
const FPS = [24, 25, 30, 50, 60];
const QUALITY = ["Draft", "Standard", "High", "Maximum"];

// Real backend job states → user-facing stage checklist.
const STAGE_FLOW = [
  { key: "PREPARING", label: "Preparing video" },
  { key: "RENDERING_TEXT", label: "Rendering captions" },
  { key: "ENCODING", label: "Encoding video" },
  { key: "MUXING", label: "Finalizing" },
  { key: "VALIDATING", label: "Validating" },
];
const clock = (s) => {
  if (s == null || isNaN(s)) return null;
  s = Math.max(0, Math.round(s));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

export default function ExportModal({ open, onOpenChange, projectId, config, captionLayers, mediaTracks, videoUrl, videoMeta, segments, duration, fitMode, letterbox, quick = false }) {
  const [settings, setSettings] = useState({ width: 1080, height: 1920, fps: 30, quality: "High" });
  const [captionMode, setCaptionMode] = useState("burned"); // burned | transparent
  const [showAdvanced, setShowAdvanced] = useState(false);
  const includeWebm = captionMode === "transparent";
  const captionOnly = captionMode === "caption_only";
  const [phase, setPhase] = useState("settings"); // settings | precheck | rendering | done | error
  const [exp, setExp] = useState(null);
  const [viewMode, setViewMode] = useState("combined");
  const [precheck, setPrecheck] = useState({ errors: [], warnings: [] });
  const [checking, setChecking] = useState(false);
  const [localAvailable, setLocalAvailable] = useState(false);
  const [v6On, setV6On] = useState(false);
  const [resolved, setResolved] = useState(false);
  const quickFired = useRef(false);
  const pollRef = useRef(null);
  const jobRef = useRef(null);
  const lsKey = `cm_export_job_${projectId}`;

  useEffect(() => {
    if (!open) return;
    (async () => { try { const d = await chooseRenderPath(); setLocalAvailable(canLocalExport() && d.localAvailable); } catch { setLocalAvailable(false); } })();
    setResolved(false); quickFired.current = false;
    api.exportResolve(projectId).then((r) => setV6On(!!r.v6)).catch(() => setV6On(false)).finally(() => setResolved(true));
  }, [open]);
  // One-tap export: start with the default (burned-in, original size) settings as soon as the engine is known.
  useEffect(() => {
    if (open && quick && resolved && phase === "settings" && !quickFired.current) { quickFired.current = true; handleRender(); }
  }, [open, quick, resolved, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Default the export to the ORIGINAL video's resolution (original-quality export).
  useEffect(() => {
    if (open && videoMeta?.width && videoMeta?.height) {
      setSettings((s) => ({ ...s, width: videoMeta.width, height: videoMeta.height }));
    }
  }, [open, videoMeta?.width, videoMeta?.height]);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  // Resume a still-running export when the modal re-opens (job keeps running server-side).
  useEffect(() => {
    if (!open) { stopPoll(); return; }
    setExp(null);
    const running = localStorage.getItem(lsKey);
    if (running) { jobRef.current = running; setPhase("rendering"); pollJob(running); }
    else setPhase("settings");
    return stopPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const applyReel = () => { setSettings({ width: 1080, height: 1920, fps: 30, quality: "High" }); toast.success("Social Reel 9:16 preset applied"); };

  const handleRender = async () => {
    // HYBRID: render the caption animation on the user's device, then let the server do the
    // heavy video+audio work. Burned → server overlays the caption layer onto video+audio.
    // Transparent → server converts the alpha layer to a transparent MOV. caption_only and
    // unsupported browsers fall back to the full server render below.
    // Export Engine V6: everything runs on the device — never the server.
    if (v6On) {
      if (!canDeviceExport()) {
        setExp({ error: "This browser can't export videos on your device. Please use Chrome or Edge." });
        setPhase("error"); return;
      }
      return runV6Export(captionMode);
    }
    if (localAvailable && videoUrl && canLocalExport()) {
      if (captionMode === "burned") return runHybridExport();
    }
    setChecking(true);
    try {
      const res = await api.validateExport(projectId, config, settings, captionLayers);
      setChecking(false);
      if (!res.ok || (res.warnings || []).length) {
        setPrecheck({ errors: res.errors || [], warnings: res.warnings || [] });
        setPhase("precheck");
        return;
      }
      startExport();
    } catch (e) {
      setChecking(false);
      startExport(); // validation is best-effort; never block a valid export
    }
  };

  // Device caption layer → upload → server composite/convert. Shared helper.
  const renderAndUploadCaptionLayer = async (stageLabel, maxPct) => {
    setPhase("rendering-local");
    setLocalState({ progress: 1, stage: stageLabel });
    localAbort.current = new AbortController();
    const cap = await exportCaptionLayerAlpha({
      layers: captionLayers, config, segments,
      width: settings.width, height: settings.height, fps: settings.fps, duration,
      signal: localAbort.current.signal,
      onProgress: (p) => setLocalState({ progress: Math.round(p.progress * (maxPct / 100)), stage: stageLabel }),
    });
    setLocalState({ progress: maxPct, stage: "Uploading caption layer" });
    const up = await api.uploadCaptionLayer(cap.blob);
    return up.storage_path;
  };

  // Normal export (Caption On Video): captions on device + video/audio composited on server.
  const runHybridExport = async () => {
    let layerPath;
    try {
      layerPath = await renderAndUploadCaptionLayer("Rendering captions on your device", 60);
    } catch (e) {
      if (e?.name === "AbortError") { setPhase("settings"); return; }
      console.warn("device caption render failed → full server render", e);
      toast.info("Finishing on the server instead.");
      return startExport();
    }
    setPhase("rendering"); setExp(null);
    try {
      const created = await api.createExport(projectId, config, settings, captionLayers, false, mediaTracks, false, layerPath, "overlay");
      if (created.status === "ready") { setExp(created); setViewMode("combined"); setPhase("done"); return; }
      jobRef.current = created.id; localStorage.setItem(lsKey, created.id); pollJob(created.id);
    } catch (e) {
      const msg = detailMsg(e); setExp({ error: msg }); setPhase("error"); toast.error(msg);
    }
  };

  // Export Engine V6: the whole MP4 (video + audio + captions) is rendered on the device.
  const v6Fail = (e, mode) => {
    if (e?.name === "AbortError") { setPhase("settings"); return; }
    const m = String(e?.message || e);
    console.warn("V6 on-device export failed", e);
    api.logExportV6({ project_id: projectId, status: "failed", error: `${mode}: ${m}`.slice(0, 300), browser: navigator.userAgent }).catch(() => {});
    const msg = m === "DEVICE_UNSUPPORTED" ? "This browser can't export videos on your device. Please use Chrome or Edge."
      : m.startsWith("SOURCE_") || m === "DECODER_UNSUPPORTED" ? "Your browser couldn't read one of the videos in this project. Please try Chrome or Edge, or re-upload the clip as MP4."
      : "The export on your device stopped unexpectedly. Your project is saved — please try again.";
    setExp({ error: msg }); setPhase("error");
  };
  const runV6Export = async (mode) => {
    setPhase("rendering-local"); setExp(null);
    setLocalState({ progress: 1, stage: "Preparing your export", v6: true });
    localAbort.current = new AbortController();
    try {
      const r = await exportTimelineOnDevice({
        mode, videoUrl, mediaTracks, layers: captionLayers, config, segments, fitMode, letterbox,
        width: settings.width, height: settings.height, fps: settings.fps, quality: settings.quality,
        duration, signal: localAbort.current.signal,
        onProgress: (p) => setLocalState({ ...p, v6: true }),
      });
      setExp({ local_url: URL.createObjectURL(r.blob), width: r.width, height: r.height, fps_out: r.fps, duration: r.duration, mp4_size: r.blob.size });
      setViewMode("combined"); setPhase("done");
      if (!r.compat) toast.info("This browser can't make standard MP4, so a different video format was used. For the widest compatibility, export in Chrome or Edge.", { duration: 9000 });
      if (mode === "burned" && !r.audioCodec) toast.info("This browser can't encode audio — the video was exported without sound. Use Chrome or Edge to include audio.", { duration: 9000 });
      api.logExportV6({ project_id: projectId, status: "complete", width: r.width, height: r.height, fps: r.fps, duration: r.duration,
        size: r.blob.size, codec: `${r.codec}${r.audioCodec ? `+${r.audioCodec}` : ""} via ${r.frameSource}`, hardware: r.hardware, frames: r.frames, encode_fps: r.encodeFps,
        total_ms: r.totalMs, audio: r.audio, browser: navigator.userAgent }).catch(() => {});
    } catch (e) { v6Fail(e, mode); }
  };
  const pollJob = (jobId) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const s = await api.exportStatus(jobId);
        setExp(s);
        if (s.status === "ready" || s.state === "COMPLETED") { stopPoll(); localStorage.removeItem(lsKey); setViewMode(s.webm_storage_path ? "overlay" : "combined"); setPhase("done"); }
        else if (s.status === "error" || s.state === "FAILED") { stopPoll(); localStorage.removeItem(lsKey); setPhase("error"); }
        else if (s.status === "cancelled" || s.state === "CANCELLED") { stopPoll(); localStorage.removeItem(lsKey); setPhase("settings"); }
      } catch { /* keep polling */ }
    }, 1000);
  };

  const startExport = async () => {
    setPhase("rendering");
    setExp(null);
    try {
      const created = await api.createExport(projectId, config, settings, captionLayers, includeWebm, mediaTracks, captionOnly);
      // Already-cached identical render → instant.
      if (created.status === "ready") { setExp(created); setViewMode(created.webm_storage_path ? "overlay" : "combined"); setPhase("done"); return; }
      jobRef.current = created.id;
      localStorage.setItem(lsKey, created.id);
      pollJob(created.id);
    } catch (e) {
      const msg = detailMsg(e); setExp({ error: msg }); setPhase("error");
      toast.error(msg);
    }
  };

  const cancelExport = async () => {
    const jobId = jobRef.current;
    if (!jobId) { setPhase("settings"); return; }
    try { await api.cancelExport(jobId); } catch { /* best effort */ }
    stopPoll(); localStorage.removeItem(lsKey);
    toast.info("Export cancelled");
    setPhase("settings");
  };

  // Local (client-side WebCodecs) export — BETA, video-only. Falls back to the server
  // renderer on any failure. Default path is still the server export button above.
  const [localState, setLocalState] = useState(null);
  const localAbort = useRef(null);
  const runLocalExport = async () => {
    if (!videoUrl) { toast.error("Local export needs the source video."); return; }
    setPhase("rendering-local");
    setLocalState({ progress: 1, stage: "Compositing frames on your device" });
    localAbort.current = new AbortController();
    try {
      const res = await exportLocal({
        videoUrl, layers: captionLayers, config, segments,
        width: settings.width, height: settings.height, fps: settings.fps,
        duration, signal: localAbort.current.signal,
        onProgress: (p) => setLocalState({ progress: p.progress, stage: "Encoding on your device", frame: p.frame, totalFrames: p.totalFrames }),
      });
      const url = URL.createObjectURL(res.blob);
      const a = document.createElement("a");
      a.href = url; a.download = `caption-motion-local.${res.ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success(`Local export done on your device (${res.codec}${res.hardware ? " HW" : ""}, ${res.encodeFps} fps, no audio)`);
      setPhase("settings");
    } catch (e) {
      if (e?.name === "AbortError") { setPhase("settings"); return; }
      console.warn("local export failed, falling back to server", e);
      toast.info("Local export unavailable — using the server renderer instead.");
      startExport();
    }
  };

  const curState = exp?.state || "QUEUED";
  const curIdx = STAGE_FLOW.findIndex((s) => s.key === curState);
  const progress = exp?.progress || 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl gap-0 overflow-hidden border-white/10 bg-[#121212] p-0" data-testid="export-modal">
        <DialogTitle className="sr-only">Export Video</DialogTitle>
        {phase === "settings" && (
          <div className="p-6">
            <div className="mb-1 flex items-center gap-2 font-display text-xl font-black tracking-tight"><Settings2 size={20} className="text-[#00F0FF]" /> Export Video</div>
            <p className="mb-5 text-sm text-[#A1A1AA]">We export at your video's <b className="text-white">original resolution</b> in high quality. Just pick how you want your captions.</p>

            {duration > 0 && (
              <div data-testid="export-timeline-duration" className="mb-4 flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs">
                <span className="font-semibold text-[#A1A1AA]">Output length (full timeline)</span>
                <span data-testid="export-timeline-duration-value" className="font-plex tabular-nums font-bold text-white">{fmtTime(duration)}</span>
              </div>
            )}

            <div className="mb-1 text-[11px] font-bold uppercase tracking-widest text-[#A1A1AA]">Caption Options</div>
            <div className="mb-5 grid gap-3" data-testid="caption-mode-select">
              <button data-testid="caption-mode-burned" onClick={() => setCaptionMode("burned")}
                className={`flex flex-col gap-2 rounded-xl border p-4 text-left transition-colors ${captionMode === "burned" ? "border-[#00F0FF] bg-[#00F0FF]/[0.08]" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#00F0FF]/15 text-[#00F0FF]"><Film size={17} /></div>
                <div className="text-sm font-bold text-white">Caption On Video</div>
                <div className="text-xs text-[#A1A1AA]">Captions burned into the video — one ready-to-post MP4.</div>
              </button>
            </div>

            <button data-testid="caption-mode-caption_only" onClick={() => setCaptionMode("caption_only")}
              className={`mb-5 flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${captionMode === "caption_only" ? "border-[#30D158] bg-[#30D158]/[0.09]" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#30D158]/18 text-[#30D158]"><Zap size={17} /></div>
              <div className="flex-1">
                <div className="flex items-center gap-2 text-sm font-bold text-white">Fast Caption-Only Export
                  <span className="rounded-full bg-[#30D158]/20 px-1.5 py-0.5 text-[10px] font-bold text-[#30D158]">FAST</span></div>
                <div className="text-xs text-[#A1A1AA]">Burns just the caption text animation over your untouched video and drops audio. Text-only re-exports finish in seconds.</div>
              </div>
            </button>

            <button data-testid="toggle-advanced" onClick={() => setShowAdvanced((v) => !v)} className="mb-3 flex items-center gap-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white">
              {showAdvanced ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Advanced options
            </button>

            {showAdvanced && (
              <div className="mb-2 space-y-4" data-testid="export-advanced">
                <button onClick={applyReel} data-testid="social-reel-preset" className="flex w-full items-center gap-3 rounded-xl border border-[#8B5CF6]/40 bg-[#8B5CF6]/[0.08] p-3 text-left transition-colors hover:bg-[#8B5CF6]/[0.14]">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#8B5CF6]/20 text-[#a78bfa]"><Film size={17} /></div>
                  <div><div className="text-sm font-bold">Social Reel 9:16</div><div className="text-xs text-[#A1A1AA]">1080×1920 · 30 FPS · High · bold word-level captions</div></div>
                </button>
                <p className="-mt-2 text-[11px] text-[#666]">Presets and the resolution grid below override your video's original resolution.</p>
                <div>
                  <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-[#A1A1AA]">Resolution</div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {RESOLUTIONS.map((r) => (
                      <button key={r.l} data-testid={`res-${r.w}x${r.h}`} onClick={() => setSettings((s) => ({ ...s, width: r.w, height: r.h }))}
                        className={`rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${settings.width === r.w && settings.height === r.h ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-white/10 bg-white/[0.02] text-[#A1A1AA] hover:border-white/25"}`}>{r.l}</button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-[#A1A1AA]">Frame rate</div>
                    <select data-testid="fps-select" value={settings.fps} onChange={(e) => setSettings((s) => ({ ...s, fps: parseInt(e.target.value) }))} className="w-full rounded-lg border border-white/10 bg-[#1a1a1a] px-3 py-2.5 text-sm outline-none">
                      {FPS.map((f) => <option key={f} value={f}>{f} FPS</option>)}
                    </select>
                  </div>
                  <div>
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-[#A1A1AA]">Quality</div>
                    <select data-testid="quality-select" value={settings.quality} onChange={(e) => setSettings((s) => ({ ...s, quality: e.target.value }))} className="w-full rounded-lg border border-white/10 bg-[#1a1a1a] px-3 py-2.5 text-sm outline-none">
                      {QUALITY.map((q) => <option key={q} value={q}>{q}</option>)}
                    </select>
                  </div>
                </div>
                {localAvailable && videoUrl && (
                  <button data-testid="local-export-btn" onClick={runLocalExport}
                    className="flex w-full items-center justify-center gap-2 rounded-full border border-[#a78bfa]/40 bg-[#a78bfa]/[0.08] py-2.5 text-xs font-bold text-[#c4b5fd] transition-colors hover:bg-[#a78bfa]/[0.16]">
                    <Zap size={14} /> Export on my device (beta · WebCodecs · no audio yet)
                  </button>
                )}
              </div>
            )}

            <button data-testid="start-export-btn" onClick={handleRender} disabled={checking} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#00F0FF] py-3.5 text-sm font-bold text-black transition-colors hover:bg-[#00d4e0] disabled:opacity-60">
              {checking ? <><Loader2 size={16} className="animate-spin" /> Checking…</> : "Export Video"}
            </button>
          </div>
        )}

        {phase === "precheck" && (
          <div className="p-6" data-testid="export-precheck">
            <div className="mb-1 flex items-center gap-2 font-display text-xl font-black tracking-tight">
              <AlertTriangle size={20} className={precheck.errors.length ? "text-red-400" : "text-[#FF9F0A]"} /> Pre-flight check
            </div>
            <p className="mb-4 text-sm text-[#A1A1AA]">
              {precheck.errors.length ? "Please fix these before rendering:" : "Heads up before we render — you can continue anyway:"}
            </p>
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {precheck.errors.map((m, i) => (
                <div key={`e${i}`} data-testid="precheck-error" className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/[0.07] px-3 py-2 text-sm text-red-300">
                  <X size={15} className="mt-0.5 shrink-0" /> {m}
                </div>
              ))}
              {precheck.warnings.map((m, i) => (
                <div key={`w${i}`} data-testid="precheck-warning" className="flex items-start gap-2 rounded-lg border border-[#FF9F0A]/30 bg-[#FF9F0A]/[0.06] px-3 py-2 text-sm text-[#f0b45a]">
                  <AlertTriangle size={15} className="mt-0.5 shrink-0" /> {m}
                </div>
              ))}
            </div>
            <div className="mt-6 flex gap-2">
              <button data-testid="precheck-back-btn" onClick={() => setPhase("settings")} className="flex-1 rounded-full border border-white/15 py-3 text-sm font-bold text-white transition-colors hover:border-white/30">Back to settings</button>
              {!precheck.errors.length && (
                <button data-testid="precheck-continue-btn" onClick={startExport} className="flex-1 rounded-full bg-[#00F0FF] py-3 text-sm font-bold text-black transition-colors hover:bg-[#00d4e0]">Render anyway</button>
              )}
            </div>
          </div>
        )}

        {phase === "rendering-local" && (
          <div className="p-8 text-center" data-testid="local-rendering">
            <Zap size={38} className="mx-auto mb-4 animate-pulse text-[#a78bfa]" />
            <h3 className="font-display text-xl font-bold">Exporting on your device</h3>
            <p className="mt-1 text-sm text-[#A1A1AA]" data-testid="local-stage">{localState?.stage || "Preparing…"}</p>
            <div className="mx-auto mt-6 max-w-md">
              <div className="mb-1 flex items-center justify-between text-xs font-semibold text-[#A1A1AA]">
                <span data-testid="local-progress-pct" className="tabular-nums text-white">{localState?.progress || 1}%</span>
                {localState?.totalFrames ? <span className="tabular-nums">{localState.frame}/{localState.totalFrames} frames</span> : null}
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-[#a78bfa] transition-all" style={{ width: `${localState?.progress || 1}%` }} /></div>
            </div>
            <p className="mt-4 text-[11px] text-[#666]">{localState?.v6 ? "Running fully in your browser — video, audio and captions. Nothing is uploaded." : "Running fully in your browser via WebCodecs. Video-only (beta) — use the standard export for audio."}</p>
            <button data-testid="local-cancel-btn" onClick={() => { localAbort.current?.abort(); }} className="mt-5 flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-[#A1A1AA] hover:text-white mx-auto"><Ban size={15} /> Cancel</button>
          </div>
        )}

        {phase === "rendering" && (
          <div className="p-8" data-testid="export-rendering">
            <div className="text-center">
              <Loader2 size={38} className="mx-auto mb-4 animate-spin text-[#00F0FF]" />
              <h3 className="font-display text-xl font-bold">Processing your video</h3>
              <p className="mt-1 text-sm text-[#A1A1AA]" data-testid="export-stage-label">CaptionMotion is {(exp?.stage || "preparing").toLowerCase().replace(/\.\.\.$/, "")}…</p>
            </div>

            {(curState === "RECOVERING" || /recover/i.test(exp?.stage || "")) && (
              <div data-testid="export-recovering-banner" className="mx-auto mt-4 max-w-md rounded-lg border border-[#00F0FF]/30 bg-[#00F0FF]/10 px-3 py-2 text-center text-xs font-semibold text-[#00F0FF]">
                Render was interrupted — resuming from your last saved checkpoint. Previous work was preserved.
              </div>
            )}

            {/* Progressive Export Preview — OFF by default (adds export-time work). Gated by Feature Control. */}
            {isFeatureEnabled("progressive_export_preview") && (() => {
              const readySec = Number(exp?.preview_seconds || 0);
              const totalSec = Number(exp?.duration || duration || 0);
              const segDone = Number(exp?.segments_done || 0);
              const segTotal = Number(exp?.segments_total || 0);
              const segCached = Number(exp?.segments_cached || 0);
              const hasVideo = !!exp?.preview_url && readySec > 0;
              const framePreviewUrl = exp?.frame_preview_url;
              const framePct = Number(exp?.frame_preview_pct || 0);
              const pct = totalSec > 0 ? Math.min(100, Math.round((readySec / totalSec) * 100)) : 0;
              return (
                <div className="mx-auto mt-5 max-w-md" data-testid="export-live-preview">
                  <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-[#00F0FF]">
                    <span className="flex items-center gap-1.5"><Film size={12} /> Export preview</span>
                    {hasVideo ? <span className="rounded-full bg-[#00F0FF]/15 px-2 py-0.5 text-[10px] text-[#00F0FF]">Live</span>
                      : framePreviewUrl ? <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-[#A1A1AA]">Rendering frames</span> : null}
                  </div>
                  <div className="mt-2 overflow-hidden rounded-xl border border-white/10 bg-black" style={{ aspectRatio: `${settings.width || 9} / ${settings.height || 16}`, height: "min(34vh,300px)", margin: "0 auto" }}>
                    {hasVideo ? (
                      <ExportPreviewPlayer url={exp.preview_url} seconds={readySec} />
                    ) : framePreviewUrl ? (
                      <img key={framePct} src={`${resolveUrl(framePreviewUrl)}${framePreviewUrl.includes("?") ? "&" : "?"}v=${framePct}`}
                        alt="Rendering preview" className="h-full w-full object-contain" data-testid="export-frame-thumb" />
                    ) : (
                      <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-center text-xs text-[#666]" data-testid="export-preview-pending">
                        <Loader2 size={20} className="animate-spin text-[#00F0FF]" />
                        Preparing the first rendered seconds…
                      </div>
                    )}
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[11px] text-[#A1A1AA]">
                    <span data-testid="export-ready-output" className="tabular-nums">
                      {hasVideo
                        ? <>Ready output: <b className="text-white">{clock(readySec) || "00:00"}</b> / {clock(totalSec) || "--:--"}</>
                        : <>Rendering frames{framePreviewUrl ? <> · <b className="text-white">{framePct}%</b></> : "…"}</>}
                    </span>
                    {segTotal > 0 ? (
                      <span data-testid="export-segments" className="tabular-nums">Segments {segDone}/{segTotal}</span>
                    ) : null}
                  </div>
                  {(readySec > 0 || segTotal > 0) ? (
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/10">
                      <div className="h-full rounded-full bg-[#22c55e] transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  ) : null}
                  {segCached > 0 ? (
                    <div className="mt-1 text-right text-[10px] text-[#6b7280] tabular-nums" data-testid="export-cache-reuse">
                      {segCached}/{segTotal} segments reused from cache
                    </div>
                  ) : null}
                </div>
              );
            })()}

            <div className="mx-auto mt-6 max-w-md">
              <div className="mb-1 flex items-center justify-between text-xs font-semibold text-[#A1A1AA]">
                <span data-testid="export-progress-pct" className="tabular-nums text-white">{progress}%</span>
                <span className="flex items-center gap-3 tabular-nums">
                  {exp?.encoding_speed ? <span className="flex items-center gap-1 text-[#00F0FF]"><Gauge size={12} /> {exp.encoding_speed}x</span> : null}
                  <span data-testid="export-elapsed">Elapsed {clock(exp?.elapsed_seconds) || "00:00"}</span>
                  <span data-testid="export-eta" className="flex items-center gap-1">
                    ETA {exp?.eta_seconds != null ? clock(exp.eta_seconds) : "Calculating…"}
                    {exp?.eta_seconds != null && exp?.eta_basis ? (
                      <span data-testid="export-eta-basis"
                        className="rounded-full bg-[#00F0FF]/12 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[#00F0FF]/80"
                        title={exp.eta_basis === "segments"
                          ? "Estimated from the real speed of recently rendered segments"
                          : "Live estimate from recent render throughput"}>
                        {exp.eta_basis === "segments" ? "from segments" : "live estimate"}
                      </span>
                    ) : null}
                  </span>
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-white/10">
                <div className="h-full rounded-full bg-[#00F0FF] transition-all" style={{ width: `${progress}%` }} />
              </div>
              {exp?.total_frames ? (
                <div className="mt-1 text-right text-[10px] text-[#666] tabular-nums" data-testid="export-frames">{exp.current_frame || 0} / {exp.total_frames} frames{exp.fps ? ` · ${exp.fps} fps` : ""}</div>
              ) : null}
            </div>

            <div className="mx-auto mt-5 max-w-xs space-y-1.5">
              {STAGE_FLOW.map((s, i) => {
                const done = curState === "COMPLETED" || (curIdx > i && curIdx !== -1);
                const active = curIdx === i;
                return (
                  <div key={s.key} data-testid={`export-stage-${s.key}`} className={`flex items-center gap-2 text-xs ${done ? "text-[#00F0FF]" : active ? "text-white" : "text-[#555]"}`}>
                    {done ? <CheckCircle2 size={13} /> : active ? <Loader2 size={13} className="animate-spin" /> : <div className="h-3 w-3 rounded-full border border-current" />} {s.label}
                  </div>
                );
              })}
            </div>

            <div className="mt-7 flex justify-center">
              <button data-testid="cancel-export-btn" onClick={cancelExport} className="flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/[0.06] px-5 py-2.5 text-sm font-semibold text-red-300 transition-colors hover:bg-red-500/[0.12]">
                <Ban size={15} /> Cancel Export
              </button>
            </div>
            <p className="mt-3 text-center text-[11px] text-[#666]">You can close this and keep editing — the export continues and you'll see it here when you return.</p>
          </div>
        )}

        {phase === "done" && exp && (() => {
          const isMov = !!(exp.mov_storage_path && !exp.mp4_storage_path);
          return (
          <div className="grid md:grid-cols-[300px_1fr]" data-testid="download-screen">
            <div className="flex items-center justify-center border-r border-white/10 bg-[#0A0A0A] p-5">
              <div className={`flex items-center justify-center overflow-hidden rounded-xl border border-white/10 ${(viewMode === "overlay" || isMov) ? "checker" : "bg-black"}`} style={{ aspectRatio: (exp.width || 9) / (exp.height || 16), height: "min(46vh,420px)" }}>
                {isMov ? (
                  <div className="flex flex-col items-center gap-2 p-4 text-center text-[#A1A1AA]" data-testid="export-mov-preview">
                    <Layers size={34} className="text-[#a78bfa]" />
                    <span className="text-xs font-semibold">Transparent caption layer</span>
                    <span className="text-[10px]">Alpha MOV · opens in your editor</span>
                  </div>
                ) : (
                  exp.local_url
                    ? <video key="local" src={exp.local_url} autoPlay loop muted playsInline className="h-full w-full object-contain" data-testid="export-preview-video" />
                    : <BlobVideo key={viewMode} path={viewMode === "overlay" ? exp.webm_storage_path : exp.mp4_storage_path} autoPlay loop muted playsInline className="h-full w-full object-contain" data-testid="export-preview-video" />
                )}
              </div>
            </div>
            <div className="p-6">
              <div className="mb-1 flex items-center gap-2 font-display text-xl font-black tracking-tight"><CheckCircle2 size={20} className="text-[#00F0FF]" /> {isMov ? "Your transparent captions are ready." : "Your video is ready."}</div>
              {exp.webm_storage_path && !isMov && (
                <div className="mb-4 flex gap-2">
                  <button onClick={() => setViewMode("combined")} className={`rounded-full px-3 py-1 text-xs font-semibold ${viewMode === "combined" ? "bg-[#00F0FF] text-black" : "border border-white/15 text-[#A1A1AA]"}`}>Over video (MP4)</button>
                  <button onClick={() => setViewMode("overlay")} className={`rounded-full px-3 py-1 text-xs font-semibold ${viewMode === "overlay" ? "bg-[#00F0FF] text-black" : "border border-white/15 text-[#A1A1AA]"}`}>Transparent overlay</button>
                </div>
              )}
              <div className="mb-5 mt-3 grid grid-cols-2 gap-2 text-sm">
                <Info l="Resolution" v={`${exp.width || settings.width}×${exp.height || settings.height}`} />
                <Info l="Frame rate" v={`${exp.fps_out || exp.fps || settings.fps} FPS`} />
                <Info l="Duration" v={fmtTime(exp.duration)} />
                <Info l={isMov ? "MOV size" : "MP4 size"} v={fmtBytes(isMov ? exp.mov_size : exp.mp4_size)} />
              </div>
              <div className="space-y-2">
                {isMov ? (
                  <a data-testid="download-mov-btn" href={mediaUrl(exp.mov_storage_path, { download: true, name: "transparent-captions.mov" })}
                    className="flex items-center justify-center gap-2 rounded-full bg-[#00F0FF] py-3 text-sm font-bold text-black transition-colors hover:bg-[#00d4e0]">
                    <Download size={16} /> Download transparent captions (MOV)
                  </a>
                ) : (
                  <a data-testid="download-mp4-btn" href={exp.local_url || mediaUrl(exp.mp4_storage_path, { download: true, name: "reel-with-captions.mp4" })}
                    download={exp.local_url ? "reel-with-captions.mp4" : undefined}
                    className="flex items-center justify-center gap-2 rounded-full bg-[#00F0FF] py-3 text-sm font-bold text-black transition-colors hover:bg-[#00d4e0]">
                    <Download size={16} /> Download MP4 (captions on video)
                  </a>
                )}
                {!isMov && <ShareVideoButton autoPrompt={quick} url={exp.local_url || mediaUrl(exp.mp4_storage_path)} name="caption-motion-reel.mp4" />}
                {exp.webm_storage_path && !isMov && (
                  <a data-testid="download-webm-btn" href={mediaUrl(exp.webm_storage_path, { download: true, name: "text-animation-alpha.webm" })}
                    className="flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.03] py-3 text-sm font-bold text-white transition-colors hover:border-white/30">
                    <Layers size={16} /> Download WebM (Alpha)
                  </a>
                )}
                <div className="flex gap-2 pt-1">
                  <button onClick={() => setPhase("settings")} className="flex flex-1 items-center justify-center gap-2 rounded-full border border-white/10 py-2.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><RotateCcw size={14} /> Create Another</button>
                  <button onClick={() => onOpenChange(false)} className="flex flex-1 items-center justify-center rounded-full border border-white/10 py-2.5 text-xs font-semibold text-[#A1A1AA] hover:text-white">Back to Editor</button>
                </div>
              </div>
            </div>
          </div>
          );
        })()}

        {phase === "error" && (
          <div className="p-8 text-center" data-testid="export-error">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-500/15 text-red-400"><AlertTriangle size={26} /></div>
            <h3 className="font-display text-lg font-bold">Export failed</h3>
            <p className="mx-auto mt-1 max-w-sm text-sm text-[#A1A1AA]">{exp?.error || "Video rendering failed. Your project is still saved. Try exporting again."}</p>
            {exp?.stage ? <p className="mt-1 text-xs text-[#666]">Failed at: {exp.stage}</p> : null}
            <button data-testid="retry-export-btn" onClick={() => setPhase("settings")} className="mt-5 rounded-full bg-white px-6 py-2.5 text-sm font-bold text-black">Retry Export</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Info({ l, v }) {
  return <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2"><div className="text-[10px] uppercase tracking-wider text-[#A1A1AA]">{l}</div><div className="font-plex text-sm font-semibold tabular-nums">{v}</div></div>;
}
